import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { style } from './blog-post.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


initializeIconsByRole({
  'csc-blog-post.author': { name: 'user', style: 'light' },
  'csc-blog-post.date': { name: 'calendar', style: 'light' }
})

interface Tag {
  name: string,
  url: string
}

@customElement('csc-blog-post')
export class CSCBlogPost extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'blog-image' })
  blogImage: string = ''

  @property({ type: String, attribute: 'fallback-image' })
  fallbackImage: string = ''

  @property({ type: String, attribute: 'blog-title' })
  blogTitle: string = ''

  @property({ type: String, attribute: 'blog-url' })
  blogUrl: string = ''

  @property({ type: String })
  category: string = ''

  @property({ type: String, attribute: 'category-url' })
  categoryUrl: string = ''

  @property({ type: String })
  summary: string = ''

  @property({ type: String })
  author: string = ''

  @property({
    type: String,
    converter: (value) => value ? new Date(value) : null
  })
  date: Date | null = null

  @property({ type: Array })
  tags: Array<Tag> = []

  @property({ type: String })
  locale: string = 'en-US'

  @property({ type: String })
  variant: 'default' | 'inline' = 'default'

  get isValidDate(): boolean {
    return this.date instanceof Date && !isNaN(this.date.getTime())
  }

  override render() {
    return html`
      <cse-card variant="${this.variant}">
        <a href="${this.blogUrl}" slot="header">
          <cse-image
            source="${this.blogImage}"
            fallback-source="${this.fallbackImage}"
            alt="${this.blogTitle}"
            fit="fill"
            height="${this.variant === 'inline' ? '250' : '200'}"
            width="${this.variant === 'inline' ? '250' : '100%'}"
          ></cse-image>
        </a>
        <div slot="body" class="content">
          ${this.category ? html`
            <cse-link url="${this.categoryUrl}" font-variant="caption-s" class="category">
              ${this.category}
            </cse-link>
          ` : nothing}
          <cse-link url="${this.blogUrl}" variant="secondary" font-variant="heading-5" class="title">
            ${this.blogTitle}
          </cse-link>
          ${this.summary ? html`
            <cse-text variant="body-s" as="span" class="summary">
              ${this.summary}
            </cse-text>
          ` : nothing}
          ${(this.author || this.isValidDate) ? html`
            <cse-list inline  id="meta">
              ${this.author ? html`
                <cse-list-item>
                  <cse-text variant="caption-m">
                    <cse-icon icon-role="csc-blog-post.author"></cse-icon>
                    ${this.author}
                  </cse-text>
                </cse-list-item>
              ` : nothing}
              ${this.isValidDate ? html`
                <cse-list-item>
                  <cse-text variant="caption-m">
                    <cse-icon icon-role="csc-blog-post.date"></cse-icon>
                    ${this.date?.getTime() ? Intl.DateTimeFormat(this.locale, { dateStyle: 'short' }).format(this.date) : ''}
                  </cse-text>
                </cse-list-item>
              ` : nothing}
            </cse-list>
          ` : nothing}
        </div>
        ${this.tags ? html`
          <div slot="footer" class="tags-container">
            <cse-list inline class="tags" id="tags">
              ${this.tags.map((tag: Tag) => html`
                <cse-list-item>
                  <cse-chip url="${tag.url}">${tag.name}</cse-chip>
                </cse-list-item>
              `)}
            </cse-list>
          </div>
        ` : nothing}
      </cse-card>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-blog-post': CSCBlogPost
  }
}
